import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AutorenewOutlined,
  CalendarTodayOutlined,
  CloseRounded,
  EmailOutlined,
  EventBusyOutlined,
  FormatListBulletedOutlined,
  LabelOutlined,
  MessageOutlined,
  WhatsApp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import HeaderPage from "../../../shared/components/HeaderPage/HeaderPage";
import NoData from "../../../shared/components/common/ui/NoData";
import { CampaignById } from "../../../services/api/Campaigns/CampaignById";
import { getSentMessagesLines } from "../../dashboard/components/indicators/services/modeGraphicModalApi";
import { mapSentMessagesRows } from "../../dashboard/components/indicators/mappers/modeGraphicModalMappers";
import dayjs from "../config/dayjs";
import { useTemplatesData } from "../hooks/useTemplatesData";
import { pageContainerSx, pageHeaderSx } from "./CampaignViewPage.styles";
import {
  buildTemplatePreview,
  createDefaultDispatchFilters,
  formatCampaignType,
  formatDateTime,
  formatPhoneNumber,
} from "./CampaignViewPage.utils";
import CampaignDetailsCard from "./components/CampaignDetailsCard";
import CampaignDispatchSection from "./components/CampaignDispatchSection";
import CampaignDispatchStatusCell from "./components/CampaignDispatchStatusCell";
import CampaignTemplatePreviewCard from "./components/CampaignTemplatePreviewCard";
import CampaignTemplatePreviewDialog from "./components/CampaignTemplatePreviewDialog";

const HIDDEN_DISPATCH_DETAIL_KEYS = new Set([
  "id",
  "action_analyze",
  "rn",
  "type",
  "month",
  "day",
  "Uuid Client",
  "uuid_client",
]);

const DISPATCH_DETAIL_SECTIONS = [
  {
    title: "Resumo",
    fields: [
      ["Canal", ["Tipo de canal", "tipo_de_canal"]],
      ["Status", ["Último Status", "ultimo_status"]],
      ["Destinatário", ["Contato destinatário", "contato_destinatario"]],
      ["Data de envio", ["Data de envio", "data_de_envio"]],
      ["ID do envio", ["ID campanha enviada", "id_campanha_enviada"]],
    ],
  },
  {
    title: "Campanha",
    fields: [
      ["Campanha", ["Título da campanha", "titulo_da_campanha"]],
      ["Modelo", ["Modelo da mensagem", "modelo_da_mensagem"]],
      ["Início", ["Data de início da campanha", "data_de_inicio_da_campanha"]],
      ["Fim", ["Data de fim da campanha", "data_de_fim_da_campanha"]],
      ["Mensagem do provedor", ["Mensagem De Erro", "mensagem_de_erro"]],
      ["ID da mensagem", ["ID Da Mensagem", "Id Da Mensagem", "id_da_mensagem"]],
      ["Ordem da mensagem", ["Ordem Da Mensagem", "ordem_da_mensagem"]],
    ],
  },
  {
    title: "Dados do item",
    fields: [
      ["Nome completo", ["Nome Completo", "nome_completo"]],
      ["Nome", ["Nome", "variavel_nome"]],
      ["Cliente ID", ["Cliente Id", "cliente_id"]],
      ["CPF/CNPJ", ["CPF/CNPJ", "CPF CNPJ", "Cpf Cnpj", "cpf_cnpj"]],
      ["E-mail", ["Email", "E-mail", "email"]],
      ["Telefone 01", ["Telefone 01", "telefone_01"]],
      ["Telefone 02", ["Telefone 02", "telefone_02"]],
      ["Telefone 03", ["Telefone 03", "telefone_03"]],
      ["Contrato ID", ["Contrato ID", "Contrato Id", "contrato_id"]],
      ["ID da fatura", ["ID Da Fatura", "Id Fatura", "id_fatura"]],
      ["Vencimento", ["Vencimento", "Data De Vencimento", "Data Vencimento", "data_vencimento", "variavel_vencimento"]],
      ["Valor", ["Valor", "variavel_valor"]],
      ["Valor total", ["Valor Total", "variavel_valor_total"]],
      ["Código de barras", ["Código De Barras", "CóDigo De Barras", "Codigo Barras", "codigo_barras"]],
      ["Linha digitável", ["Linha Digitável", "Linha Digitavel", "variavel_linha_digitavel"]],
      ["Código PIX", ["Codigo Pix", "Código Pix", "PIX", "variavel_pix"]],
      ["WhatsApp", ["WhatsApp", "variavel_whatsapp"]],
      ["Link PDF", ["Link Pdf", "link_pdf"]],
      ["Referência 01", ["Referência 01", "variavel_referencia_01"]],
      ["Referência 02", ["Referência 02", "variavel_referencia_02"]],
      ["Referência 03", ["Referência 03", "variavel_referencia_03"]],
      ["Referência 04", ["Referência 04", "variavel_referencia_04"]],
      ["Referência 05", ["Referência 05", "variavel_referencia_05"]],
    ],
  },
];

function normalizeDispatchKey(key) {
  return String(key || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
}

function isFilledDispatchValue(value) {
  if (value === null || value === undefined) return false;

  const normalizedValue = String(value).trim().toLowerCase();
  return normalizedValue !== "" &&
    normalizedValue !== "-" &&
    normalizedValue !== "none" &&
    normalizedValue !== "null" &&
    normalizedValue !== "não informado";
}

function getDispatchDetailField(row, keys) {
  const entriesByNormalizedKey = new Map(
    Object.entries(row || {}).map(([key, value]) => [
      normalizeDispatchKey(key),
      { key, value },
    ]),
  );

  const matchedKeys = [];

  for (const key of keys) {
    const directValue = row?.[key];

    if (isFilledDispatchValue(directValue)) {
      matchedKeys.push(key);
      return { value: directValue, matchedKeys };
    }

    const normalizedMatch = entriesByNormalizedKey.get(normalizeDispatchKey(key));

    if (normalizedMatch && isFilledDispatchValue(normalizedMatch.value)) {
      matchedKeys.push(normalizedMatch.key);
      return {
        value: normalizedMatch.value,
        matchedKeys,
      };
    }
  }

  return { value: null, matchedKeys };
}

function getDispatchDetailValue(row, keys) {
  return getDispatchDetailField(row, keys).value;
}

function formatDispatchDetailLabel(key) {
  return String(key)
    .replace(/^variavel_/i, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getDispatchDetailSignature(label, value) {
  return `${normalizeDispatchKey(label)}::${String(value).trim().toLowerCase()}`;
}

function getGroupedDispatchDetails(row) {
  if (!row) {
    return { sections: [], otherFields: [] };
  }

  const usedKeys = new Set();
  const usedNormalizedKeys = new Set();
  const usedValueSignatures = new Set();
  const sections = DISPATCH_DETAIL_SECTIONS
    .map((section) => {
      const fields = section.fields
        .map(([label, keys]) => {
          const { value, matchedKeys } = getDispatchDetailField(row, keys);

          if (!isFilledDispatchValue(value)) {
            return null;
          }

          const normalizedKeys = [...keys, ...matchedKeys].map(normalizeDispatchKey);
          const normalizedLabel = normalizeDispatchKey(label);

          if (
            normalizedKeys.some((key) => usedNormalizedKeys.has(key)) ||
            usedValueSignatures.has(getDispatchDetailSignature(label, value))
          ) {
            return null;
          }

          keys.forEach((key) => {
            usedKeys.add(key);
            usedNormalizedKeys.add(normalizeDispatchKey(key));
          });
          matchedKeys.forEach((key) => {
            usedKeys.add(key);
            usedNormalizedKeys.add(normalizeDispatchKey(key));
          });
          usedNormalizedKeys.add(normalizedLabel);
          usedValueSignatures.add(getDispatchDetailSignature(label, value));

          return { label, value };
        })
        .filter(Boolean);

      return { ...section, fields };
    })
    .filter((section) => section.fields.length);

  const otherFields = Object.entries(row)
    .filter(([key, value]) =>
      !HIDDEN_DISPATCH_DETAIL_KEYS.has(key) &&
      !usedKeys.has(key) &&
      !usedNormalizedKeys.has(normalizeDispatchKey(key)) &&
      isFilledDispatchValue(value)
    )
    .map(([key, value]) => ({
      label: formatDispatchDetailLabel(key),
      value,
    }))
    .filter((field) => {
      const normalizedLabel = normalizeDispatchKey(field.label);
      const signature = getDispatchDetailSignature(field.label, field.value);

      if (usedNormalizedKeys.has(normalizedLabel) || usedValueSignatures.has(signature)) {
        return false;
      }

      usedNormalizedKeys.add(normalizedLabel);
      usedValueSignatures.add(signature);
      return true;
    });

  if (otherFields.length) {
    const itemSection = sections.find((section) => section.title === "Dados do item");

    if (itemSection) {
      itemSection.fields = [...itemSection.fields, ...otherFields];
    } else {
      sections.push({
        title: "Dados do item",
        fields: otherFields,
      });
    }
  }

  return { sections, otherFields: [] };
}

function DispatchDetailValue({ label, value, compact = false }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        backgroundColor: "#f8fafc",
        px: 1.25,
        py: compact ? 0.75 : 1,
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#0f172a",
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.35,
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
        }}
      >
        {String(value)}
      </Typography>
    </Box>
  );
}

function CampaignViewSkeleton() {
  return (
    <Box sx={pageContainerSx}>
      <Stack mb={5} mt={5}>
        <Skeleton variant="text" width={90} height={22} />
        <Skeleton variant="text" width="42%" height={46} sx={{ maxWidth: 520 }} />
      </Stack>

      <Grid container spacing={3} alignItems={{ xs: "flex-start", lg: "stretch" }}>
        <Grid item xs={12} lg={7} xl={8}>
          <Box
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              backgroundColor: "#ffffff",
              p: 2.25,
              minHeight: 300,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Skeleton variant="text" width={180} height={28} />
              <Skeleton variant="rounded" width={96} height={34} sx={{ borderRadius: 2 }} />
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1.25,
              }}
            >
              {Array.from({ length: 9 }).map((_, index) => (
                <Box key={index}>
                  <Skeleton variant="text" width="45%" height={18} />
                  <Skeleton variant="rounded" width="88%" height={26} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
            </Box>

            <Skeleton variant="text" width={160} height={22} sx={{ mt: 2.25 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
                gap: 0.75,
                mt: 0.75,
              }}
            >
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={54}
                  sx={{ borderRadius: "10px" }}
                />
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} lg={5} xl={4}>
          <Box
            sx={{
              height: { xs: 360, sm: 380, lg: 396 },
              border: "1px solid #dde3ec",
              borderRadius: "10px",
              backgroundColor: "#f8fafc",
              overflow: "hidden",
              p: 2,
            }}
          >
            <Stack direction="row" spacing={1} mb={2}>
              {[0, 1, 2].map((item) => (
                <Skeleton
                  key={item}
                  variant="rounded"
                  width={96}
                  height={28}
                  sx={{ borderRadius: "999px" }}
                />
              ))}
            </Stack>
            <Stack spacing={1.25}>
              <Skeleton variant="rounded" height={72} sx={{ borderRadius: "10px" }} />
              <Skeleton
                variant="rounded"
                height={44}
                width="82%"
                sx={{ borderRadius: "10px", alignSelf: "flex-end" }}
              />
              <Skeleton variant="rounded" height={96} sx={{ borderRadius: "10px" }} />
              <Skeleton variant="rounded" height={38} width="68%" sx={{ borderRadius: "10px" }} />
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 3,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          backgroundColor: "#ffffff",
          p: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={1}
          mb={2}
        >
          <Box>
            <Skeleton variant="text" width={110} height={26} />
            <Skeleton variant="text" width={190} height={18} />
          </Box>
          <Skeleton variant="circular" width={36} height={36} />
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1,
            mb: 2,
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={40} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            height={42}
            sx={{ borderRadius: 0.8, mb: 0.75 }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function CampaignViewPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    templates,
    loading: loadingTemplates,
    isFetching: isFetchingTemplates,
  } = useTemplatesData();

  const defaultDispatchFilters = useMemo(() => createDefaultDispatchFilters(), []);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [dispatchRows, setDispatchRows] = useState([]);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchError, setDispatchError] = useState("");
  const [dispatchPaginationModel, setDispatchPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [dispatchRowCount, setDispatchRowCount] = useState(0);
  const [dispatchApiPage, setDispatchApiPage] = useState(1);
  const [dispatchApiPageSize, setDispatchApiPageSize] = useState(0);
  const [dispatchReloadKey, setDispatchReloadKey] = useState(0);
  const [selectedDispatchRow, setSelectedDispatchRow] = useState(null);
  const [isDispatchDetailsOpen, setIsDispatchDetailsOpen] = useState(false);
  const [dispatchDraftFilters, setDispatchDraftFilters] = useState(defaultDispatchFilters);
  const [dispatchAppliedFilters, setDispatchAppliedFilters] = useState(defaultDispatchFilters);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const dispatchApiPageSizeRef = useRef(0);

  const locationCampaign = useMemo(() => {
    if (location.state?.campaign?.id === id) {
      return location.state.campaign;
    }

    return null;
  }, [id, location.state]);

  const campaignViewData = useMemo(() => {
    if (!campaignDetails && !locationCampaign) {
      return null;
    }

    const mergedCampaign = {
      ...locationCampaign,
      ...campaignDetails,
    };

    return {
      ...mergedCampaign,
      id: campaignDetails?.code || locationCampaign?.id || id,
      title: campaignDetails?.title || locationCampaign?.title || "",
      whatsapp_id: campaignDetails?.whatsapp_id ?? locationCampaign?.whatsapp_id,
      sms_id: campaignDetails?.sms_id ?? locationCampaign?.sms_id,
      email_id: campaignDetails?.email_id ?? locationCampaign?.email_id,
      Visibility:
        typeof mergedCampaign.visibility === "boolean"
          ? mergedCampaign.visibility
            ? "Visível"
            : "Oculto"
          : mergedCampaign.Visibility || "-",
    };
  }, [campaignDetails, id, locationCampaign]);

  const campaignType = formatCampaignType(
    campaignViewData?.campaign_type || campaignViewData?.type,
  );

  const campaignTemplateIds = useMemo(
    () =>
      [
        campaignViewData?.model_whatsapp_id,
        campaignViewData?.model_sms_id,
        campaignViewData?.model_email_id,
      ].filter(Boolean),
    [
      campaignViewData?.model_email_id,
      campaignViewData?.model_sms_id,
      campaignViewData?.model_whatsapp_id,
    ],
  );

  const hasConfiguredTemplates = campaignTemplateIds.length > 0;

  const campaignTemplatePreviews = useMemo(() => {
    const safeTemplates = Array.isArray(templates) ? templates : [];

    return campaignTemplateIds
      .map((templateId) =>
        safeTemplates.find(
          (template) => String(template.id) === String(templateId),
        ),
      )
      .filter(Boolean)
      .map(buildTemplatePreview);
  }, [campaignTemplateIds, templates]);

  const selectedTemplatePreview =
    campaignTemplatePreviews.find(
      (template) => template.templateId === selectedTemplateId,
    ) ||
    campaignTemplatePreviews[0] ||
    null;
  const dispatchDetails = useMemo(
    () => getGroupedDispatchDetails(selectedDispatchRow),
    [selectedDispatchRow],
  );

  const activeTemplateId = selectedTemplatePreview?.templateId || false;
  const isLoadingTemplatePreview = loadingTemplates || isFetchingTemplates;
  const isPageLoading = campaignLoading;
  const canOpenPreviewModal =
    hasConfiguredTemplates &&
    !isLoadingTemplatePreview &&
    selectedTemplatePreview &&
    selectedTemplatePreview.messages.length > 0;

  const dispatchColumns = useMemo(
    () => [
      {
        field: "Tipo de canal",
        headerName: "Canal",
        minWidth: 90,
        flex: 0.45,
        sortable: false,
        renderCell: (params) => {
          const channel = String(params.value || "").toLowerCase();

          if (channel.includes("whatsapp")) {
            return (
              <Tooltip title="WhatsApp" arrow>
                <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                  <WhatsApp sx={{ color: "#25D366", fontSize: 20 }} />
                </Box>
              </Tooltip>
            );
          }

          if (channel.includes("sms")) {
            return (
              <Tooltip title="SMS" arrow>
                <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                  <MessageOutlined sx={{ color: "#2563eb", fontSize: 20 }} />
                </Box>
              </Tooltip>
            );
          }

          if (channel.includes("email") || channel.includes("e-mail")) {
            return (
              <Tooltip title="E-mail" arrow>
                <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                  <EmailOutlined sx={{ color: "#0ea5e9", fontSize: 20 }} />
                </Box>
              </Tooltip>
            );
          }

          return "-";
        },
      },
      {
        field: "Data de envio",
        headerName: "Data de envio",
        minWidth: 160,
        flex: 0.9,
      },
      {
        field: "Contato destinatário",
        headerName: "Destinatário",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "Último Status",
        headerName: "Status",
        minWidth: 180,
        flex: 1,
        renderCell: (params) => <CampaignDispatchStatusCell value={params.value} />,
      },
      {
        field: "action_analyze",
        headerName: "Ações",
        width: 132,
        sortable: false,
        align: "right",
        headerAlign: "center",
        renderCell: (params) => (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              onClick={() => {
                setSelectedDispatchRow(params.row);
                setIsDispatchDetailsOpen(true);
              }}
              sx={{
                minWidth: 94,
                height: 28,
                borderRadius: "999px",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1,
                px: 1.15,
                py: 0,
                color: "#0f172a",
                backgroundColor: "#cbd5e1",
                "&:hover": {
                  backgroundColor: "#b7c4d6",
                },
              }}
            >
              Visualizar
            </Button>
          </Box>
        ),
      },
    ],
    [],
  );

  const campaignChannelDetails = useMemo(() => {
    const channels = campaignDetails?.channels || {};

    return [
      {
        label: "WhatsApp",
        value: channels.whatsapp
          ? formatPhoneNumber(channels.whatsapp.channel_identifier)
          : "",
      },
      {
        label: "SMS",
        value: channels.sms
          ? channels.sms.channel_identifier
            ? formatPhoneNumber(channels.sms.channel_identifier)
            : "Padrão"
          : "",
      },
      {
        label: "E-mail",
        value: channels.email
          ? `${channels.email.channel_identifier || "-"}`
          : "",
      },
    ];
  }, [campaignDetails]);

  const campaignMetrics = [
    {
      label: "Criada em",
      icon: <CalendarTodayOutlined sx={{ width: 24, height: 24 }} />,
      value: (() => {
        const createdAtValue =
          campaignViewData?.created_at ||
          campaignViewData?.createdAt;

        return createdAtValue ? formatDateTime(createdAtValue, null) : "----";
      })(),
    },
    {
      label: "Tipo de campanha",
      icon: <AutorenewOutlined sx={{ width: 24, height: 24 }} />,
      value: (() => {
        const startDate = campaignViewData?.start_date;
        const expirationDate = campaignViewData?.expiration_date;

        if (!startDate || !expirationDate) return "---";

        return dayjs(startDate).isSame(dayjs(expirationDate), "day")
          ? "Disparo único"
          : "Recorrente";
      })(),
    },
    {
      label: "Etiqueta",
      icon: <LabelOutlined sx={{ width: 24, height: 24 }} />,
      value: campaignType || "-",
    },
    {
      label: "Listagem",
      icon: <FormatListBulletedOutlined sx={{ width: 24, height: 24 }} />,
      value:
        campaignViewData?.list_title ||
        campaignViewData?.listTitle ||
        "----",
    },
    {
      label: "Início",
      icon: <CalendarTodayOutlined sx={{ width: 24, height: 24 }} />,
      value: formatDateTime(
        campaignViewData?.start_date,
        campaignViewData?.start_time,
      ),
      valueSx: {
        whiteSpace: "nowrap",
      },
    },
    {
      label: "Expiração",
      icon: <EventBusyOutlined sx={{ width: 24, height: 24 }} />,
      value: formatDateTime(
        campaignViewData?.expiration_date,
        campaignViewData?.start_time,
      ),
      valueSx: {
        whiteSpace: "nowrap",
      },
    },
  ];

  const dispatchSettings = useMemo(() => {
    const toBool = (value) => {
      if (typeof value === "boolean") return value;
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        return ["1", "true", "sim", "yes"].includes(normalized);
      }
      return Boolean(value);
    };

    const dynamicDateEnabled = toBool(campaignViewData?.dynamic_date);

    const rawReferenceMoment = String(campaignViewData?.reference_moment || "")
      .trim()
      .toLowerCase();
    const referenceMomentMap = {
      before: "Antes",
      after: "Depois",
      on_day: "No dia",
      onday: "No dia",
      day: "No dia",
      same_day: "No dia",
    };
    const referenceMomentLabel = referenceMomentMap[rawReferenceMoment] || "----";

    const daysCalculate = campaignViewData?.days_calculate;
    const referenceDateValue = dynamicDateEnabled
      ? daysCalculate !== undefined && daysCalculate !== null && daysCalculate !== ""
        ? `${daysCalculate} Dias ${referenceMomentLabel}`
        : referenceMomentLabel
      : "Não";

    const businessDaysValue =
      !dynamicDateEnabled
        ? "Não"
        : campaignViewData?.business_days !== undefined
          ? toBool(campaignViewData.business_days)
            ? "Sim"
            : "Não"
          : "----";

    const blockDynamicEnabled = campaignViewData?.block_dynamic !== undefined
      ? toBool(campaignViewData.block_dynamic)
      : false;
    const filterFieldValue =
      campaignViewData?.block_dynamic !== undefined
        ? blockDynamicEnabled ? "Sim" : "Não"
        : "----";

    return [
      { label: "Filtra data", value: dynamicDateEnabled ? "Sim" : "Não" },
      { label: "Referência data", value: referenceDateValue },
      { label: "Filtra campo", value: filterFieldValue },
      { label: "Somente dias úteis", value: businessDaysValue },
    ];
  }, [campaignViewData]);

  const paginatedDispatchRows = useMemo(() => {
    if (!dispatchRows.length) {
      return [];
    }

    const gridPageSize = dispatchPaginationModel.pageSize;
    const globalStartIndex = dispatchPaginationModel.page * gridPageSize;

    if (!dispatchApiPageSize) {
      return dispatchRows.slice(0, gridPageSize);
    }

    const apiStartIndex = (dispatchApiPage - 1) * dispatchApiPageSize;
    const localStartIndex = Math.max(0, globalStartIndex - apiStartIndex);

    return dispatchRows.slice(localStartIndex, localStartIndex + gridPageSize);
  }, [
    dispatchApiPage,
    dispatchApiPageSize,
    dispatchPaginationModel.page,
    dispatchPaginationModel.pageSize,
    dispatchRows,
  ]);

  const dispatchTargetApiPage = useMemo(() => {
    if (dispatchApiPageSize <= 0) {
      return 1;
    }

    return (
      Math.floor(
        (dispatchPaginationModel.page * dispatchPaginationModel.pageSize) /
          dispatchApiPageSize,
      ) + 1
    );
  }, [
    dispatchApiPageSize,
    dispatchPaginationModel.page,
    dispatchPaginationModel.pageSize,
  ]);

  useEffect(() => {
    dispatchApiPageSizeRef.current = dispatchApiPageSize;
  }, [dispatchApiPageSize]);

  useEffect(() => {
    if (!campaignTemplatePreviews.length) {
      if (selectedTemplateId) {
        setSelectedTemplateId("");
      }

      if (isPreviewModalOpen) {
        setIsPreviewModalOpen(false);
      }

      return;
    }

    const stillExists = campaignTemplatePreviews.some(
      (template) => template.templateId === selectedTemplateId,
    );

    if (!stillExists) {
      setSelectedTemplateId(campaignTemplatePreviews[0].templateId);
    }
  }, [campaignTemplatePreviews, isPreviewModalOpen, selectedTemplateId]);

  useEffect(() => {
    let isMounted = true;

    const loadCampaignDetails = async () => {
      if (!id) return;

      try {
        setCampaignLoading(true);
        const response = await CampaignById(id);

        if (!isMounted) return;

        setCampaignDetails(response?.campaign || null);
      } catch (error) {
        if (!isMounted) return;

        console.error("Erro ao carregar detalhes da campanha:", error);
        setCampaignDetails(null);
      } finally {
        if (isMounted) {
          setCampaignLoading(false);
        }
      }
    };

    loadCampaignDetails();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    const loadDispatchRows = async () => {
      if (!campaignViewData?.title) return;

      setDispatchLoading(true);
      setDispatchError("");

      try {
        const body = {
          start_date: dayjs(dispatchAppliedFilters.startDate).format(
            "DD/MM/YYYY",
          ),
          end_date: dayjs(dispatchAppliedFilters.endDate).format("DD/MM/YYYY"),
          refresh: true,
          page: dispatchTargetApiPage,
          page_size: dispatchPaginationModel.pageSize,
          name_campaign: campaignViewData.title,
          filter_status: dispatchAppliedFilters.status.length
            ? dispatchAppliedFilters.status
            : null,
          filter_channel: dispatchAppliedFilters.channel.length
            ? dispatchAppliedFilters.channel
            : null,
        };

        const response = await getSentMessagesLines(body);

        if (!isMounted) return;

        const apiRows = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const inferredApiPageSize =
          dispatchApiPageSizeRef.current ||
          apiRows.length ||
          dispatchPaginationModel.pageSize;
        const formattedRows = mapSentMessagesRows(
          apiRows,
          body.page,
          inferredApiPageSize,
        );

        setDispatchRows(formattedRows);
        setDispatchRowCount(response?.data?.total_items || 0);
        setDispatchApiPage(dispatchTargetApiPage);
        setDispatchApiPageSize((current) =>
          current > inferredApiPageSize ? current : inferredApiPageSize,
        );
      } catch (error) {
        if (!isMounted) return;

        console.error("Erro ao carregar disparos da campanha:", error);
        setDispatchRows([]);
        setDispatchRowCount(0);
        setDispatchApiPage(1);
        setDispatchError("Não foi possível carregar os disparos da campanha.");
      } finally {
        if (isMounted) {
          setDispatchLoading(false);
        }
      }
    };

    loadDispatchRows();

    return () => {
      isMounted = false;
    };
  }, [
    campaignViewData?.title,
    dispatchAppliedFilters.channel,
    dispatchAppliedFilters.endDate,
    dispatchAppliedFilters.startDate,
    dispatchAppliedFilters.status,
    dispatchPaginationModel.pageSize,
    dispatchTargetApiPage,
    dispatchReloadKey,
  ]);

  const handleDispatchDraftFilterChange = (field, value) => {
    setDispatchDraftFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleApplyDispatchFilters = () => {
    setDispatchApiPage(1);
    setDispatchApiPageSize(0);
    setDispatchPaginationModel((current) => ({
      ...current,
      page: 0,
    }));
    setDispatchAppliedFilters(dispatchDraftFilters);
  };

  const handleClearDispatchFilters = () => {
    const emptyFilters = createDefaultDispatchFilters();

    setDispatchDraftFilters(emptyFilters);
    setDispatchAppliedFilters(emptyFilters);
    setDispatchApiPage(1);
    setDispatchApiPageSize(0);
    setDispatchPaginationModel((current) => ({
      ...current,
      page: 0,
    }));
  };

  if (isPageLoading) {
    return <CampaignViewSkeleton />;
  }

  if (!campaignLoading && !campaignViewData) {
    return (
      <Box width="100%" minHeight="100vh">
        <HeaderPage
          breadcrumbs={[{ label: "Campanhas" }, { label: "Visualizar" }]}
        />
        <NoData
          title="Campanha não encontrada"
          description="Não foi possível localizar a campanha selecionada."
          buttonText="Voltar para campanhas"
          onClick={() => navigate("/campanhas")}
        />
      </Box>
    );
  }

  return (
    <Box sx={pageContainerSx}>
      <Stack direction={{ xs: "row" }} mb={5} mt={5}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 0, mb: 0.25, lineHeight: 1.1 }}
            >
              Campanha
            </Typography>

            <Typography variant="h4" sx={pageHeaderSx}>
              {campaignViewData?.title || "Campanha"}
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: "0.6em",
                  color: "#64748b",
                  fontWeight: 500,
                }}
              >
                ({campaignViewData?.desc || campaignViewData?.description || "-"})
              </Typography>
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Grid container spacing={3} alignItems={{ xs: "flex-start", lg: "stretch" }}>
        <Grid item xs={12} lg={7} xl={8}>
          <CampaignDetailsCard
            campaignMetrics={campaignMetrics}
            dispatchSettings={dispatchSettings}
            status={campaignViewData?.status}
            channels={campaignChannelDetails}
          />
        </Grid>

        <Grid item xs={12} lg={5} xl={4}>
          <CampaignTemplatePreviewCard
            campaignTemplatePreviews={campaignTemplatePreviews}
            activeTemplateId={activeTemplateId}
            selectedTemplatePreview={selectedTemplatePreview}
            hasConfiguredTemplates={hasConfiguredTemplates}
            isLoadingTemplatePreview={isLoadingTemplatePreview}
            canOpenPreviewModal={canOpenPreviewModal}
            onSelectTemplate={setSelectedTemplateId}
            onOpenPreviewModal={() => setIsPreviewModalOpen(true)}
          />
        </Grid>
      </Grid>

      <CampaignDispatchSection
        dispatchDraftFilters={dispatchDraftFilters}
        dispatchRows={paginatedDispatchRows}
        dispatchColumns={dispatchColumns}
        dispatchRowCount={dispatchRowCount}
        dispatchLoading={dispatchLoading}
        dispatchError={dispatchError}
        dispatchPaginationModel={dispatchPaginationModel}
        onDraftFiltersChange={handleDispatchDraftFilterChange}
        onApplyFilters={handleApplyDispatchFilters}
        onClearFilters={handleClearDispatchFilters}
        onReload={() => setDispatchReloadKey((current) => current + 1)}
        onPaginationModelChange={setDispatchPaginationModel}
      />

      <CampaignTemplatePreviewDialog
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        campaignTemplatePreviews={campaignTemplatePreviews}
        activeTemplateId={activeTemplateId}
        selectedTemplatePreview={selectedTemplatePreview}
        hasConfiguredTemplates={hasConfiguredTemplates}
        isLoadingTemplatePreview={isLoadingTemplatePreview}
        onSelectTemplate={setSelectedTemplateId}
      />

      <Dialog
        open={isDispatchDetailsOpen}
        onClose={() => setIsDispatchDetailsOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              Dados do disparo
            </Typography>
            {selectedDispatchRow && (
              <Typography
                sx={{
                  mt: 0.25,
                  color: "#64748b",
                  fontSize: 13,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getDispatchDetailValue(selectedDispatchRow, ["Contato destinatário", "contato_destinatario"]) ||
                  "Destinatário não informado"}
              </Typography>
            )}
          </Box>

          <IconButton
            size="small"
            onClick={() => setIsDispatchDetailsOpen(false)}
            sx={{ color: "#64748b" }}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, backgroundColor: "#ffffff" }}>
          {!selectedDispatchRow ? (
            <Typography color="text.secondary">Nenhum dado disponível.</Typography>
          ) : (
            <Stack spacing={2}>
              {dispatchDetails.sections.map((section) => (
                <Box key={section.title}>
                  <Typography
                    sx={{
                      mb: 1,
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#334155",
                      textTransform: "uppercase",
                      letterSpacing: 0,
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1,
                    }}
                  >
                    {section.fields.map((field) => (
                      <DispatchDetailValue
                        key={`${section.title}-${field.label}`}
                        label={field.label}
                        value={field.value}
                      />
                    ))}
                  </Box>
                </Box>
              ))}

            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
